import {
  assertEvent,
  assign,
  fromPromise,
  sendTo,
  setup,
  type SnapshotFrom,
} from 'xstate';
import type {IEvent} from '../../types/IEvent';
import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getReasons,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';

import {rootMachineAnalyticsActions} from './rootMachine.analytics';


export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getReasons: fromPromise(async () => getReasons()),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        const meetingCoordinatorHash = await getShortSha256Hash(
          `meeting_coordinator_${input.eventId}`,
        );
        if (meetingCoordinatorHash === input.attendee) {
          return 'MEETING_COORDINATOR';
        }
        const interviewerHash = await getShortSha256Hash(
          `interviewer_${input.eventId}`,
        );
        if (interviewerHash === input.attendee) {
          return 'INTERVIEWER';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
          aid: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; reason: string; aid: string};
      }) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return (context.eventDetails as IEvent)?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellationFlow: ({context}) => {
      return context.currentRoute === 'cancel';
    },
    isRescheduleAndNotSequential: ({context}) => {
      const sequentialEvent =
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id);
      return context.currentRoute === 'reschedule' && !sequentialEvent;
    },
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!(context.eventDetails as IEvent)?.did_cancellation_notice_pass,
    isValidAttendeeAvailable: ({context}) =>
      !!context.attendee &&
      ['CANDIDATE', 'HOST', 'INTERVIEWER', 'MEETING_COORDINATOR'].includes(
        context.owner || '',
      ),
    isHostOrInterviewer: ({context}) =>
      context.owner === 'HOST' || context.owner === 'INTERVIEWER',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
    isSequentialEvent: ({context}) => {
      // Check if eventDetails is an array  for booking sequence OR event has group_id for any edit booking
      return (
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id)
      );
    },
    isRequestMoreAvailabilitySuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'RequestMoreAvailabilitySuccess';
    },
  },
  actions: {
    ...rootMachineAnalyticsActions,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAHADYAzOSYBOAExMALBLEB2KWMkKANCACeiALSbNE+duVKlszQFYmEgL6vDaTOVgY8ybGY2JBBOHj4BIVEEUzEZdXIZBRlHCxkpKQks1UMTGMcZOXUlVQySnSltd090DB8-AKwGMWCOLl5+QRDo0wlHJXJHZyYZCUVpKQUJXLMCoolyqXKxSqlqkC86339ApVbQ9oiu0GiV8lUlZQ0rqbixGfylKwvNC4VNJk-Stw8N2vI+ABbMAALwEYAAMqg8BBIE1WEIwh1It0zNpnk5rjIVBIFP0HqYUkx5GIFKoZDYxKpkqp1ptyMgwHhYAJYFCYdwiFgIODyJyAG6oADWYAZ-0ZzNZ7IgnKgCAFqAAxnROkEgojDp0orMznFVOS7N91DljGZClJyE5RhTJGICkxNHTxUyWQQ2dCZVyeQRRQqRWLvBLXe6OUR5QRBcqjmqWhrwlrUfldfEDeUJMaCaNVJaChIbRp7Y7fvSg1KPbLuby-aKSy6y6G5Qqo6rWAw9nHkccRDqLXrU0b0wSDTnrZpbYWnYG627pRWwMg0MhyFR8BgAGaoZCAgN1Usz8thpsqgRqhEhJFHbVJ3sp7FpjOmmLqYlWvNjgsyB2T3fT2AAUQXTcsEZDBkCMdVz01FETlmCxyD6J5P2tKRPikAknisV98ztT8ixqbwgVBcEAAVuEVEVkCwQiwR9HwwAwCC2njaDuxiClHHIKRSgKFCmCUeI7DQx9TFUD5Bk0KY7CyO882-cgwH5MACAwWcvSrCNhRrf4FKUlSD0bDTmxPVszyYzsr1MXNzi41QCnsWQ7E0Al+kw3NsInYttMU5TVKgLB50XZdVw3Lcd3k7y9IbcNI2PAhT32C8Exg-JRms-U7IyO8nOE1ILSw98cK-TzvFgOAeAEXzK1o6swtK2ByoIXzoqVWL4o7S9E2cMRyC0EZFHGJg9EcB4KSsOw1DEB1VEUJ45LqhrKoCzcgroELt3pebOiao9oxMhKoK7U4+IURJCnsxwFDEJQnAeFQOOxJJUk0S4qTxOSdOUgAVMBARXOhIX0qrfQ0-16Q+jBvt+1cAainaWxYRiDmYw7xEmbMeMmvjVCpJxsryPMBiYC6KUmZ7cscd6Ishv76EWwClxptawvB6noe2wzWr29qktYpQ7QtGQqSURx9RSaaFCEvIpBF8gFGUK7HLJSQKeKupYXoLdOTAAB5AB3H1KO9YHBVB-51fnQEtb1g3mqMuKucg5GLP6TR4NEwbLAuj5BYJJZs3y8dcMp3TfIAxdgPosDEcSlieie+CXOUCxBqGaZhOJex0i4wp0me15Bbmsqto9OFo4Oq91CsSpjvsbG+Ikh5KkwyRpau7RRZkQv6uLmFS9jR3zMTKl4kSQbhekMYCjxxBMjkT4aR0PFRK0LuGrDoCQKj0ykcH5K8w45O8X6EZJj5h4hZ64WibJS6FlJOSACN0CFWUADEcFQXWgb5EHRSf4U34f11mXJ2Q8uJWDlp+ak6QlZknPldbq7tbi4gyBIV4j9n6AM-t-Gq-8X5EHfp-Zo+1QHJRUBScg40bSLz4ukeBKhziOC0NibQKRM4YIAQQoBODf7kDwVg4B7YB4dTIY4JYPUyaTWUIoJY9DiS2WYddWQ-RZAcPwVAQhX98BEFIBQagtB6AgN3rzXqnFpDxAKBLfeMgHjpgGKSWyg1yTTSWHhP43gWY-RpmAdelFN7gW3jHFGCByFz2GCLZIygDQPBFt1HE5oNAn36HJT+BtpSl0CeXRMvQdDwRKJ8JgqwdALAeO8Dik17JZDUNaTuqtyCpPnOkiATR+5mREaxeIxI5ZyySZka4Jo8hTHkRJPoeIVgEyUCk-WjSS7NLbCQ4x0QFAOk4isXiOh1By3uI+IZ5wJKaHiPnZ6986kNOQE0poMgFntNOBSOQ2J1AtySFaSWiAyk9T4iLaagtrqXDUbKAAysQRUioyrwmuTzU4Y4TpaCeOSV4csRZpzyL1dGsguJ50uCLWp+E6gwANv9AAghgegBBYRgB4SbLS3h8XziJSSpS5LbacwRpk0hrEtAZESB8ZwlRrpjlkY+UkTDxK2AdKkFQtI6nKgIKCnAq5OiaJ-rwcF3NY5mFegfa6TD0yjFxLZc+n5uqFLUAsfiEtlFyRlXKhVAglWchVcQtVwTYh82zPESorxvgOjtIawa8gULFHNZlFWuLyDWrAPK2K9qCCOqEW0yFZgxgDCJAsT5z1xh+uNYGs1SQQ1WsIDa6NQCGTTlfpubca0I6SgIOW0KsBiAP0tgxNliyNWSFdv0a67wLCOA0AaoVfarAuFGkkQWHwFgFtlZG21taS17jrduBtTbeB8DUtVXh9II1RqOEqhdFafCNubWugyMVdqsoheqmIugxiyxUO8EY+ouJqHPniAWHxFBvteJkNxW7C0zuLZ-UtNbF2HpXSSuc9MVrrgPX+6dO7FXzrLQe5dx7ZTMvPUYm5aIJaX0UNjMWKxBrn0XvILFcRx5Ypxe4uo27Z17uQ6FJafjGNLqPbwLDiaYjJCKBhDC6gxGpHPkMbqljUg8cuskNYdTGSwEVAAC0gMQHAYAlU4G4M2gAcpgAASkyBTGTL3BIcecEYqRUjSCGNLc+T7R6lEeSUK6ySZNwAMxAZTqmS3qa07p-Tim5mtJ3thhAKiSR9uXtLW4AzxAXHuU8ApcsdCkl-c6OT-mPNqY07wbTGA9N4Dc00eNQWuMSX9iUaa5JRiWB9kK8kJ1LgOlxA6ZZF05KybcxlpDIGD1Vv3fW9jLajMWS5ZNDQkwsaoqUOfPM-thVKOkcshQbXXPpZUwx7r-XwMnspZpMK7XVueaA31tjW30Nw2Mhe51V4Rb2MyMs9IcwDkkekJfZOfMLDqALi5tLSm1tdddKB1Dq7IOBUZrB1LHW-tHdY2BtDh4OaYdbcFgoB8yS4j7Xaew593yDDtAhB0KFA3LZ++5qHutgMA4Pcx6tlPNvNs41elxnFKikgo4NRr2OuVML7cfbQJqpVhv279w75P+FcOwUbH+VK+GYPF8ApHXG4gSVx+VvEhSynbJRUw7qFJvlRJkakYnkORcy84Ro7hkvcGy-N0QwLQSrykkFqPSkUkSjSGEyUWWFxyQe0k3zI3B2lVi5t1owgJAyCin0f9Bnxm1CE2UHqpwhSG5Cp0BxWJF0+YuCWLJb7xuY2OoV1e0w4jeolHsCMLZshz4Nfgk1-i0sUE-EFyt4XBfAh26yWQ-ilDXhOGWbiC1XEa-LLr01yYljKjUfpMHgAwv+nAi6sBz4X4DgbMerxJGJFSO07wJbXUFgYR872rC6BpHLSwByUveBX-BoFp313G12zP638+78DZPRh+GG-slNcSFxAoTVHOaeEJFQAYC6CVDQchKoOpW-OVe-OHPyZjaDJmF-M3N-eAj-M7BHb-IvF1XEc4aacTSwRxeIGxY-T9TiFwbICkfvMQf5IgDAyNXxZfV-VfFDdfPAiyBReCdIbGLIWwXQKbCg8kN2JYTIUoImA5ZvGjcgOgUlclAAcSUjpXoGaSF1J1FCgFQB-2SipFvQOQcQuH3iuleQQEkBSFlmWQ+EuG0BejknkMZTADAGUIJTUKwDo1inIG0N0I5VEldkGkFj6B5Q0EHGPxUGNTxC9QPypDEXcF+AIFQFhHgBCE2Cu2yRWFH1GHITxwWB5wJDHAGGFiq0+EKKgLmgaAwHSOSliAugtAlj4iJjtFoVEmck-E4g+zsCunHUGjkmIDdGoE4ACEgAACE0BdZSpkBqjWJYgyCJE+gmFtAPgpgQCRJ-UGshhFtc4xgBdZDqJwQmlpiegNBupJpRIqs+gJpVj9RuorELg+YJhb5lsa0QxPQoAjik1SQ9kT53gBxosYhhZTjqQHQPhShPxpZpMW8XjfEPinwe9zQCcB8PVSh0IsgRw3xA4iow19ifRSJyJ5xYSroA171Bp0xLoXAhxRIqDsQygp8tldiwYIpfJYTehPcbiU5tAHJrphM5BgjytYtdAnBQ1ZDNoKp9JYTHkJFygiYIknhtBpsigVhdAcIHoyRg4vovE2ZxThFFdJhjVsQR0iDlYj8pZxF0x+pcpPhTC5JzZNYfRrYCSdTi9rhVla4ngvYRhNdEALpt954BNYUUx1TIo3iYSnSXVyQOJnwDkRNFsxwCQM50Vs5zQ85SgMhV4e5YQIAJS5YeoUJDCksLAzDFjLQR1zVrRJpr8tgi4BBQyE0r1JpsQzEXoxFv03cbN7Er4LpqRJAHiGT-hg9NFCS+UeomEyQQSHR7BhDBlqREgVFxwFh0ogzWZ-pazisr13s55BpMhZBuyMgYlShLR+I0gMc6sltTlplzlZkWTLBMJbIJ50hc4ChbEZZSQ0EsVcobJC4ABHYgXSbgPAHAP8CKP8GUDAHLQleVT+SAQkwoY1VCC-FraaEA0bDiVCbEKxSaK6dMms4QKgbgRkLMsMiuNBShSoKYbFA5RyYaR8T2U6K6dKQofZM8sNYPIFEFMqQkg5OQfoN4NXPMLGEjf1Uo4WcWAnVrOpWlZAelBQ5w2ElQSoHqCw4eaaBYZFcQKTQgjIM-MWNMupRIvgNcIwNi0FeqOSvtV2c0C6TIiwHlYTFwWWXGa6HGB8vsqcH8uADAAAWU3DAEJX5DwG4HwAfkCt4CMuBRMpSLrOCT5gUqQqgKWG6JNPEFsMGBQiSGcW0A7SnSLV3SARZJWAuESDTUFiHXHmopRVfNzLHTQXfGSGFLgxysQyAwdSqKIoyK7KKpyNKvyKFUuCsESSsymnQWlQX3oxLU8KOGIlQHU0VDyCiosliKsDEXhVgsgTUvMOHAcGhRpIJmyoA1yuhw20BHyv4nT0VhspPPKpi0bMcSGHSqkycD2oQztX+ztR6wrXyqVLr1NX1Fsj6rMLtC+LVxcnUC0osCerGsOtpxO0QM+q5QfPL1xHsiJhI31HEiFj7RcFmhGvg0hvJ2O3knphZMqA4gTygL5i7RRMHRFTEwkkKEk3SAhsA3J24FhGUm4DXDImZpZIgMoTzCCLM1eBFhrz6GZySDiE-EunhQDzbzyrapqJEqsOkieFnjUGs16v9TiEuiFKEKThls0Myx81yz82gvlpMWzGSEUCJHAWcHWriDzCKomlsgyDHEkH1s6yhreq3BZPkqVoJnvLVquvMNkGNX5huIwmoPdrJwpy9srQ+rNp6Dxz9uxADu4mm1SC9xQjzD5RbKjpNwJqBwgyIEJJliJmuhHUx14iDskGmlOiFskHNLjzzvW2hsJsXFhIhJHOmklURUnOx2egTmVnNTxwOWbpLQHLlvmsTDTy9ynhpEKQkOE0KucEQhziKRkNrBJw9pZrZoMq5oOt1kJKxgDTYUBIknHhsziEGCQuzmHjxA3oh0DxLRavyrxHkGyP334uxBFqri3zIucFsiSDHuhy3pUwhCy2Nvy38x5sQXu2UGSH4n1DyJszUDdnFoWOjJ0Da1gH5CoEHITsQCRtSoSwuE+CEyFUKQGCcB0FdoWBgQfpvzYPg0XSPqJkGEsQ+zHEvyStAKEqiP1BiNKAYKgCYJwAQOB2LoIZiFbkSE5WKGejlnUFui0E7TfUGglmpHCyeuMo4qkdiA7S92kF0BKv1S9JCVJBOnTFyjYR6QYbqDgOYKJr0aumJHMu6IWEulSBSFumqTEPsHTBQi8chNkL03ct8D01AbAB0dMqkaiMoQqUvztEq1fUsOllqouEmFGGYtkMcLJWcNcNUNNqnr0LBOZzVwXqQfIZRXeAGDGEDRKB6TiHiNcCAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
    releaseFlags: input.releaseFlags,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'reasonsLoading',
        },
      ],
    },

    reasonsLoading: {
      invoke: {
        id: 'rootNew.reasonsLoading:invocation[0]',
        input: () => ({}),
        onDone: [
          {
            target: 'eventLoading',
            guard: {
              type: 'isEditBooking',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'sessionLoading',
            guard: {
              type: 'isSession',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'eventTemplateLoading',
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
        ],
        onError: {
          target: 'reasonsError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getReasons',
      },
    },

    reasonsError: {
      on: {
        retry: {
          target: 'reasonsLoading',
        },
      },
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'determineOwner',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if ((context.eventDetails as IEvent)?.id) {
            return {
              eventId: (context.eventDetails as IEvent)?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      on: {
        'language.update': {
          actions: [
            assign({
              language: ({event}) => event.language,
            }),
            sendTo(
              ({system}) => system.get('bookingMachine')!,
              ({event}) => ({
                type: 'language.update',
                language: event.language,
              }),
            ),
          ],
        },
      },
      invoke: {
        id: 'bookingFlow',
        input: ({
          context: {
            timezone,
            eventTemplate,
            language,
            session,
            rescheduleSource,
            releaseFlags,
          },
        }) => {
          if (timezone) {
            return {
              timezone,
              rescheduleSource,
              eventTemplate,
              language,
              session,
              releaseFlags,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'requestMoreAvailabilitySuccess',
            guard: {
              type: 'isRequestMoreAvailabilitySuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellationFlow',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isRescheduleAndNotSequential',
          },
          actions: assign({rescheduleSource: 'confirmation_email'}),
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
        {
          guard: {
            type: 'isSequentialEvent',
          },
          target: 'sequentialEventEditNotAllowed',
        },
      ],
      entry: assign({
        language: ({context}) => {
          const eventDetails = context.eventDetails as IEvent;
          const users =
            context.owner === 'HOST' || context.owner === 'INTERVIEWER'
              ? eventDetails?.interviewers
              : eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sequentialEventEditNotAllowed: {
      type: 'final',
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      always: 'generateAttendee',
    },

    generateAttendee: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: (eventDetails as IEvent)?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },

    notifySuccess: {
      type: 'final',
    },

    requestMoreAvailabilitySuccess: {
      type: 'final',
    },

    cancellationFlow: {
      entry: [
        assign({cancellationSource: 'confirmation_email'}),
        'trackCancelMeetingInitiated',
      ],
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isValidAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id) {
                    return {
                      eventId: eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      entry: 'trackRescheduleMeetingInitiated',
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHostOrInterviewer',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id && context.aid) {
                    return {
                      eventId: eventDetails.id,
                      reason: event.reason || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId or aid not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          on: {
            'language.update': {
              actions: [
                assign({
                  language: ({event}) => event.language,
                }),
                sendTo(
                  ({system}) => system.get('bookingFlow'),
                  ({event}) => ({
                    type: 'language.update',
                    language: event.language,
                  }),
                ),
              ],
            },
          },
          invoke: {
            id: 'bookingFlow',
            input: ({context}) => {
              const {
                timezone,
                attendee,
                language,
                rescheduleSource,
                releaseFlags,
              } = context;
              if (timezone) {
                return {
                  timezone,
                  rescheduleSource,
                  eventDetails: context.eventDetails as IEvent,
                  attendee,
                  language,
                  releaseFlags,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                  reason: ({event}) => event.output.rescheduleReason,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {aid} = context;
          const eventDetails = context.eventDetails as IEvent;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      entry: 'trackCancelMeetingInitiated',
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          const eventDetails = context.eventDetails as IEvent;
          assertEvent(event, 'bookingCancelForm.submit');

          const normalizedEvent = Array.isArray(eventDetails)
            ? eventDetails[0]
            : eventDetails;

          if (!normalizedEvent?.id) {
            throw new Error('eventId not found');
          }

          return {
            eventId: normalizedEvent.id,
            reason: event.reason,
            viewerTimezone: normalizedEvent.primary_guest_timezone || '',
            attendee: context.attendee || '',
            aid: normalizedEvent.candidates?.[0]?.id || context.aid || '',
          };
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
      entry: 'trackCancelMeetingConfirmed',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
          actions: assign({rescheduleSource: 'post_booking_page'}),
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
          actions: assign({cancellationSource: 'post_booking_page'}),
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
